// What the approving device decides about a code it has just read (#1693,
// spec section 6.1, decision 15).
//
// WHY THIS IS A MODULE AND NOT A COMPONENT METHOD. The acceptance criterion
// for the out-of-band secret is that a public key which does not match the
// scanned one is REFUSED BEFORE ANYTHING IS SENT. That is a decision, not a
// rendering, and a decision worth asserting on its own: this file has no Vue,
// no store and no API, so `approval-scan.spec.js` can put a substituted key in
// front of it and read the answer without mounting anything.
//
// DELIBERATELY NOT IN `courses-crypto/`. Nothing here derives, wraps or
// unwraps: it parses a payload, compares two byte strings, and names a state
// for the screen. Keeping it out of that directory is what lets this change
// leave every crypto module alone.
import {
  APPROVAL_SECRET_ERRORS,
  parseApprovalPayload,
  secretFromTypedCode,
} from '@/utils/courses-crypto/approval-secret';
import { base64ToBytes } from '@/utils/courses-crypto/encoding';
import { isSupported, startScan } from '@/utils/qr-scanner';

/**
 * The one vocabulary the two halves of screen 3 share: what the approver's
 * check is doing, or what it decided. `ApprovalCheck.vue` renders one sentence
 * per value and `PendingDeviceRequestHost.vue` enables Approve on three of
 * them, so a state added here without copy is visible immediately.
 */
export const APPROVAL_CHECK = Object.freeze({
  /** Nothing read yet. Approve is disabled. */
  IDLE: 'idle',
  /** The camera is open. */
  SCANNING: 'scanning',
  /** A scan whose device id and public key both matched the row. */
  MATCHED: 'matched',
  /** Sixteen Crockford characters that parsed. The key was not checked — see below. */
  TYPED: 'typed',
  /** The explicit "this phone is on an older version" fallback: a `v: 1` wrap. */
  OLDER_VERSION: 'older-version',
  /** Decision 15's refusal: the scanned key is not the key the API presented. */
  REFUSED_KEY: 'refused-key',
  /** A real approval code, from a different phone than the one asking. */
  WRONG_DEVICE: 'wrong-device',
  /** Not an approval code at all, or one whose fields do not parse. */
  MALFORMED: 'malformed',
  /**
   * Sixteen valid characters that do not agree with each other: the member
   * mistyped one (`approval-secret.js`, the check character). Caught HERE,
   * before anything is approved, rather than on the other phone as a wrapping
   * that will not open.
   */
  CHECK_FAILED: 'check-failed',
  /** The member closed the camera without reading anything. */
  CANCELLED: 'cancelled',
  /** No camera, permission refused, or a build with no scanner at all. */
  UNAVAILABLE: 'unavailable',
});

/**
 * Byte-wise, never a string compare.
 *
 * `device-key.js`'s `wrapAad` already gives the reason in its own comment: it
 * binds "the recipient's key BYTES (not their base64 rendering, which a
 * transport is free to re-encode)". The same holds here, and concretely — an
 * SPKI is 91 bytes, which is not a multiple of three, so the final base64
 * character carries four bits that no byte uses. Any of sixteen characters
 * there decodes to the same key and compares unequal as text.
 *
 * Everywhere else that would be a curiosity. Here it would be a FALSE REFUSAL
 * on the one screen where a false refusal is unrecoverable: the member is told
 * their own phone may be someone else's, and the only way forward the screen
 * offers is Deny.
 *
 * @param {Uint8Array} a
 * @param {Uint8Array} b
 * @returns {boolean}
 */
function equalBytes(a, b) {
  if (!(a instanceof Uint8Array) || !(b instanceof Uint8Array)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * @param {unknown} publicKey base64 SPKI
 * @returns {Uint8Array | null} null when it is not base64 at all
 */
function decodeKey(publicKey) {
  try {
    return base64ToBytes(publicKey);
  } catch (error) {
    return null;
  }
}

/**
 * What the camera read, against the row the API presented.
 *
 * THE ORDER IS LOAD-BEARING, and each step is here for its own reason.
 *
 * 1. Parse first. A QR that is not an approval code at all — a boarding pass,
 *    the church's own /scan/ code — must not be reported as a key mismatch,
 *    which is the most alarming sentence this screen owns.
 * 2. The DEVICE ID before the key. A member who picked up the wrong phone and
 *    scanned their spouse's screen is told it is a different phone, which is
 *    both true and actionable. Checking the key first would tell them instead
 *    that the key does not match — an accusation, for a mistake.
 * 3. Only then the key, byte-wise (`equalBytes` says why). This is decision
 *    15: a server that substituted the pending device's public key between the
 *    registration and this read is caught here, before anything is sent.
 *
 * @param {string} text what the camera read
 * @param {{ id: string, publicKey: string }} device the PENDING row as the API gave it
 * @returns {{ state: string, secret?: Uint8Array, publicKey?: string }} the 16
 *   bytes and the VERIFIED public key on a match, so the caller wraps to the
 *   key it checked rather than to the row's copy of it
 */
export function verifyScannedPayload(text, device) {
  let parsed;
  try {
    parsed = parseApprovalPayload(text);
  } catch (error) {
    if (error?.code === APPROVAL_SECRET_ERRORS.MALFORMED_PAYLOAD) {
      return { state: APPROVAL_CHECK.MALFORMED };
    }
    throw error;
  }

  if (parsed.deviceId !== device?.id) return { state: APPROVAL_CHECK.WRONG_DEVICE };

  const scanned = decodeKey(parsed.publicKey);
  const registered = decodeKey(device?.publicKey);
  if (!scanned || !registered || !equalBytes(scanned, registered)) {
    return { state: APPROVAL_CHECK.REFUSED_KEY };
  }

  return {
    state: APPROVAL_CHECK.MATCHED,
    secret: parsed.secret,
    publicKey: parsed.publicKey,
  };
}

/**
 * The typed carrier: fifteen Crockford characters standing for the first
 * seventy-five bits of the same S, and a check character over them.
 *
 * THE CODE IS CHECKED AGAINST ITSELF, AND AGAINST NOTHING ELSE. A member typing
 * a code has read no public key, so there is none to compare — what protects
 * them against a substituted key is that S goes into the wrapping key itself,
 * so the waiting phone cannot open what a wrong key produced. The refusal of
 * decision 15 is the scanned path's extra; the typed path keeps the guarantee
 * without it. What the check character adds is the member's own typo: sixteen
 * valid characters that do not agree with each other are refused here, where
 * the member can fix them, and not on the other phone after the approve.
 *
 * @param {string} input exactly what the member typed, separators and all
 * @returns {{ state: string, secret?: Uint8Array }}
 */
export function verifyTypedCode(input) {
  try {
    return { state: APPROVAL_CHECK.TYPED, secret: secretFromTypedCode(input) };
  } catch (error) {
    if (error?.code === APPROVAL_SECRET_ERRORS.CHECK_FAILED) {
      return { state: APPROVAL_CHECK.CHECK_FAILED };
    }
    if (error?.code === APPROVAL_SECRET_ERRORS.MALFORMED) {
      return { state: APPROVAL_CHECK.MALFORMED };
    }
    throw error;
  }
}

const DEFAULT_DEPS = Object.freeze({ startScan, isSupported });

/**
 * Whether this build can open a camera at all. The web harness cannot, and the
 * screen has to offer the typed path alone rather than a Scan button that
 * throws when it is tapped.
 *
 * Never rejects, for the same reason `scanApprovalCode` does not: the caller is
 * a `mounted()` hook with nothing to catch a throw.
 *
 * @param {{ deps?: object }} [args]
 * @returns {Promise<boolean>}
 */
export async function scanAvailable({ deps } = {}) {
  try {
    return (await (deps?.isSupported ?? DEFAULT_DEPS.isSupported)()) === true;
  } catch (error) {
    return false;
  }
}

/**
 * Open the camera and decide about what it read. NEVER REJECTS: every failure,
 * including a member who backed out, comes back as a state — the caller is a
 * tap handler on a modal, and a throw would leave Approve disabled for ever
 * with nothing on screen saying why.
 *
 * `startScan()` returns `parseQrData(content)`, and an approval payload has no
 * `type` field, so that function classes it as `{ type: 'unknown', raw, data }`
 * — hence `raw`, the text exactly as the camera read it, rather than `data`.
 * Anything else it recognises (an SSO approval, a /scan/ URL) also carries
 * `raw`, and falls through to MALFORMED, which is what it is here.
 *
 * @param {{ device: { id: string, publicKey: string }, deps?: object }} args
 * @returns {Promise<{ state: string, secret?: Uint8Array, publicKey?: string }>}
 */
export async function scanApprovalCode({ device, deps: overrides } = {}) {
  const scan = overrides?.startScan ?? DEFAULT_DEPS.startScan;
  let result;
  try {
    result = await scan();
  } catch (error) {
    // No camera, permission denied, or the web build. The member is not told
    // to check a setting they may not have — the screen offers the typed code.
    console.warn('[CoursesE2EE] The approval code could not be scanned:', error);
    return { state: APPROVAL_CHECK.UNAVAILABLE };
  }
  // `null` is the plugin's own "the member closed the scanner": not a failure,
  // and nothing on the screen should suggest it was.
  if (!result) return { state: APPROVAL_CHECK.CANCELLED };
  return verifyScannedPayload(result.raw, device);
}
